// The five objections (funds / timing / partner / uncertainty), as instant
// in-the-moment reference cards. From the Call Playbook.

export type ObjectionCard = {
  id: string;
  trigger: string; // what they say
  category: "funds" | "timing" | "partner" | "uncertainty";
  firstMove: string;
  lines: { label: string; text: string }[];
  demoNumber?: boolean;
  never?: string;
  partnerWarning?: boolean;
};

export const OBJECTIONS: ObjectionCard[] = [
  {
    id: "funds",
    trigger: "That's more than I was thinking",
    category: "funds",
    firstMove:
      "Isolate. \"If that weren't an issue, is anything else stopping you moving forward today?\"",
    lines: [
      {
        label: "The split",
        text: "When you say it's more than you were thinking — is it that the fifteen hundred isn't there right now, or that you're not sure yet it'll work?",
      },
      {
        label: "If doubt → their math",
        text: "Three months is forty-five hundred. Your average ticket's {{ticket}}. So this pays for itself if it catches you a couple of cars in ninety days — and you told me you're missing that many in a fortnight.",
      },
      {
        label: "If genuinely cash",
        text: "That's exactly what the four ninety-seven trial is for.",
      },
    ],
    never: "Never discount. Not by a dollar. The moment the price moves, they've learned everything was negotiable — including whether it works.",
  },
  {
    id: "uncertainty",
    trigger: "How do I know it'll work?",
    category: "uncertainty",
    firstMove: "Reframe the burden of proof. This is the strongest single argument available.",
    lines: [
      {
        label: "The reframe",
        text: "The last lot had to go out and create demand for you — convince strangers on Facebook to want a body shop. That's genuinely hard and most of the time it doesn't work. You found that out for eighteen grand. That's not this. Those calls going to voicemail — those people already want you. They found you, they picked up the phone. Nobody has to be convinced of anything. The only question is whether someone answers. That's a much smaller thing to be right about.",
      },
      {
        label: "Then demo it",
        text: "Don't take my word for it. Get your phone out, call our demo line right now, and be a customer with a dented quarter panel. Then tell me if you'd have booked.",
      },
    ],
    demoNumber: true,
  },
  {
    id: "history",
    trigger: "I tried marketing before and it didn't work",
    category: "uncertainty",
    firstMove: "Not an objection — an invitation. Get curious. The details are the material to close on.",
    lines: [
      { label: "Dig", text: "Tell me more about that. What did they actually do for you?" },
      { label: "Dig", text: "How long did you give it? Was any of it working, or none of it?" },
      {
        label: "The gold",
        text: "What would you need to see this time to know it was different?",
      },
    ],
  },
  {
    id: "partner",
    trigger: "I need to talk to my partner",
    category: "partner",
    firstMove: "Hearing this at the close means a mistake 20 minutes earlier. Recover:",
    lines: [
      { label: "Recover", text: "Makes sense. What specifically would {{partnerName}} want to know?" },
      { label: "Recover", text: "What would he push back on hardest?" },
      {
        label: "Three-way",
        text: "Rather than you having to make my case for me — what does Thursday look like for the three of us?",
      },
    ],
    never: "Never send a PDF and leave. That deal is dead.",
    partnerWarning: true,
  },
  {
    id: "think",
    trigger: "Let me think about it",
    category: "timing",
    firstMove: "Almost always hides a real objection that never came up.",
    lines: [
      {
        label: "Uncover",
        text: "Course. What is it you want to think about specifically?",
      },
      { label: "Then", text: "Solve that one, then ask again." },
    ],
  },
];

// ShopFillPro Voice AI demo line ("Maya"). PLACEHOLDER — set the real number here
// before using the live-demo objection close; the 555-01xx block is not a working line.
export const DEMO_NUMBER = "";
export const DEMO_NUMBER_PLACEHOLDER = "set your Voice AI demo line";

export const DISQUALIFICATION = {
  title: "When it isn't a fit — say so",
  body: "Some shops shouldn't buy this. Fleet-heavy operations with no consumer phone volume. Shops too small to sustain $1,500 without pain. Owners who won't touch the CRM. Closing them costs more than walking away.",
  script:
    "{{ownerName}}, I'm going to be straight with you — I don't think this is right for you, and here's why. [reason]. I'd rather tell you that than take your money. Can I ask you something though — do you know other shop owners who are doing walk-in and consumer work? That's exactly who this fits.",
  note: "A clean disqualification plus a referral ask beats a bad close every time. Logged as a WIN, not a loss.",
};
